<template>
  <div>
    <div
      class="flex flex-wrap items-center gap-1.5 border rounded-lg px-2 py-1.5 min-h-[42px] bg-white"
      :class="[disabled ? 'bg-gray-50' : 'focus-within:ring-2 focus-within:ring-[#22282F]/20', erreur ? 'border-red-300' : 'border-gray-300']"
    >
      <span
        v-for="(valeur, idx) in modelValue"
        :key="valeur"
        class="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-sm rounded-md pl-2.5 pr-1 py-0.5"
        data-test="chip"
      >
        {{ valeur }}
        <button
          v-if="!disabled"
          type="button"
          class="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
          :aria-label="`Retirer ${valeur}`"
          @click="retirer(idx)"
        >
          <X class="w-3 h-3" />
        </button>
      </span>
      <input
        v-if="!disabled"
        v-model="saisie"
        type="text"
        :placeholder="modelValue.length ? '' : placeholder"
        class="flex-1 min-w-[10rem] bg-transparent outline-none text-sm py-1 px-1"
        data-test="chip-input"
        @keydown.enter.prevent="ajouter"
        @keydown="onKey"
        @blur="ajouter"
        @paste="onPaste"
      />
    </div>
    <p v-if="erreur" class="text-xs text-red-500 mt-1">{{ erreur }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { X } from 'lucide-vue-next'

const props = defineProps<{
  modelValue: string[]
  placeholder?: string
  disabled?: boolean
  // Renvoie un message d'erreur si la valeur est refusée, sinon rien.
  validate?: (valeur: string) => string | null | undefined
}>()
const emit = defineEmits<{ (e: 'update:modelValue', valeurs: string[]): void }>()

const saisie = ref('')
const erreur = ref<string | null>(null)

const comparable = (v: string) => v.trim().toLowerCase()

function ajouterValeurs(brutes: string[]) {
  const valeurs = [...props.modelValue]
  erreur.value = null
  for (const brute of brutes) {
    const valeur = brute.replace(/\s+/g, ' ').trim()
    if (!valeur) continue
    const refus = props.validate?.(valeur)
    if (refus) {
      erreur.value = refus
      continue
    }
    if (!valeurs.some((v) => comparable(v) === comparable(valeur))) valeurs.push(valeur)
  }
  emit('update:modelValue', valeurs)
}

function ajouter() {
  if (!saisie.value.trim()) return
  const refuses = saisie.value
  ajouterValeurs([saisie.value])
  // Une valeur refusée reste dans le champ pour être corrigée.
  saisie.value = erreur.value ? refuses : ''
}

// « , » et « ; » valident la saisie comme Entrée.
function onKey(e: KeyboardEvent) {
  if (e.key === ',' || e.key === ';') {
    e.preventDefault()
    ajouter()
  } else if (e.key === 'Backspace' && !saisie.value && props.modelValue.length) {
    retirer(props.modelValue.length - 1)
  }
}

// Un collage de plusieurs valeurs (séparées par virgule, point-virgule ou retour) les ajoute toutes.
function onPaste(e: ClipboardEvent) {
  const texte = e.clipboardData?.getData('text') || ''
  if (!/[,;\n]/.test(texte)) return
  e.preventDefault()
  ajouterValeurs(texte.split(/[,;\n]+/))
  saisie.value = ''
}

function retirer(idx: number) {
  erreur.value = null
  emit('update:modelValue', props.modelValue.filter((_, i) => i !== idx))
}
</script>
